import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SyllabusQuery } from '@examdojo/category/v2';
import { environment } from '@examdojo/core/environment';
import { FeatureFlagService } from '@examdojo/core/feature-flag';
import { PosthogErrorHandlingService } from '@examdojo/core/posthog';
import { AuthService, UserQuery } from '@examdojo/user';
import { combineLatest, filter, first, map, merge, switchMap, take, tap } from 'rxjs';

@Injectable()
export class PosthogUserTraitsService {
  constructor(
    private readonly authService: AuthService,
    private readonly userQuery: UserQuery,
    private readonly posthogErrorHandlingService: PosthogErrorHandlingService,
    private readonly syllabusQuery: SyllabusQuery,
    private readonly featureFlagService: FeatureFlagService,
  ) {
    merge(this.setIdentityOnLogin(), this.setTraitsAfterOnboarding(), this.setCourse(), this.resetOnLogout())
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  private readonly onboardedUser$ = this.userQuery.onboardedUser$.pipe(first(Boolean));

  private setIdentityOnLogin() {
    return this.userQuery.active$.pipe(
      first(Boolean),
      tap((user) => {
        this.posthogErrorHandlingService.identifyUser(user.id, {
          email: user.email,
          app_version: environment.release_version,
        });
      }),
      switchMap(() => this.featureFlagService.fetch()),
    );
  }

  private setTraitsAfterOnboarding() {
    return this.onboardedUser$.pipe(
      tap((user) => {
        this.posthogErrorHandlingService.setUserTraits({
          first_name: user.first_name,
          last_name: user.last_name,
        });
      }),
      switchMap(() => this.featureFlagService.fetch()),
    );
  }

  private setCourse() {
    return combineLatest([this.onboardedUser$, this.syllabusQuery.active$]).pipe(
      map(([user, syllabus]) => {
        if (syllabus) {
          return {
            syllabus: syllabus.name,
            course_level: user.course_level,
          };
        }

        return;
      }),
      filter(Boolean),
      take(1),
      tap((traits) => {
        this.posthogErrorHandlingService.setUserTraits(traits);
      }),
    );
  }

  private resetOnLogout() {
    return this.authService.hasLoggedOut$.pipe(
      tap(() => {
        this.posthogErrorHandlingService.resetUser();
      }),
    );
  }
}
